import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom'
import { Button, Nav, NavItem } from 'reactstrap';
import { Utils, canShowSyncImportPanel } from '../../../../services';
import {
    readSidebarSectionOpenMap,
    writeSidebarSectionOpenMap,
} from '../../../../services/kronosPrefs/userInstancePrefs';
import { loadWorkflows } from '../../../../redux/modules/workflows';
import Config from '../../../../config/Config';
import gitBranch from '../../../../config/gitBranch';
import TktSyncButton from '../../../../components/TktSyncButton/TktSyncButton';
import InstanceSelector from '../../../../components/InstanceSelector/InstanceSelector';
import { updateUi } from '../../../../redux';
import { visibleNavigation, groupIdsForPathname } from '../../../../config/navigation';

import './SideBar.scss';

/** Sub-paths of /settings/edit/:id/ that map to other sidebar items; keep aligned with SPECIAL_ROUTE_FIELDS in SettingEdit. */
const SETTINGS_ROUTES_WITH_OWN_NAV_ITEM = ['images', 'fonts', 'mobile', 'edition', 'retention', 'integrations', 'default_layouts'];

function is_settings_main_nav_active(pathname) {
    if (!pathname.startsWith('/settings/edit/'))
        return false;
    const after_instance = pathname.replace(/^\/settings\/edit\/[^/]+\/?/, '');
    const segments = after_instance.split('/').filter(Boolean);
    if (segments.length === 0)
        return true;
    return !SETTINGS_ROUTES_WITH_OWN_NAV_ITEM.includes(segments[0]);
}

function SidebarGroupHeading({ collapsed, group_id, is_open, is_current, on_toggle, label, children }) {
    return (
        <button
            type="button"
            className={`sidebar-group-toggle sidebar-heading d-flex ${collapsed ? 'justify-content-center' : 'justify-content-between'} px-3 title-category${is_current ? ' sidebar-heading--current' : ''}`}
            onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                on_toggle(group_id);
            }}
            aria-expanded={is_open}
            aria-current={is_current ? 'true' : undefined}
            aria-label={label}
            title={collapsed ? label : undefined}
        >
            {children}
            {!collapsed && (
                <i
                    className={`fa sidebar-group-chevron ${is_open ? 'fa-chevron-down' : 'fa-chevron-right'}`}
                    aria-hidden="true"
                />
            )}
        </button>
    );
}

function resolve_label(item, t) {
    if (item.label)
        return item.label;
    if (item.labelKey)
        return t(item.labelKey, item.labelOptions);
    return '';
}

const SideBar = ({ section, onCollapseNavClick, collapsed, children }) => {
    const { pathname }  = useLocation();
    const { t }         = useTranslation();
    const dispatch      = useDispatch();
    const { user }      = useSelector(state => state.auth);
    const { setting }   = useSelector(state => state.settings);
    const { workflows } = useSelector(state => state.workflows);

    const { isMobile, showChangelog, highlightChangelog } = useSelector(state => state.ui);

    const tkt_instance = user?.tkt_instance;

    // Context passed to every navigation visibility predicate.
    const nav_ctx = useMemo(
        () => ({ user, tkt_instance, setting, workflows, pathname }),
        [user, tkt_instance, setting, workflows, pathname]
    );

    // Filtered view of the navigation tree: invisible groups/items are removed
    // up front so the rendering loop is a straight iteration.
    const tree = useMemo(() => visibleNavigation(nav_ctx), [nav_ctx]);
    const current_section_tree = useMemo(
        () => tree.find(s => s.sectionId === section),
        [tree, section]
    );

    const sidebar_prefs_scope = useMemo(() => {
        if (!user?.id) {
            return null;
        }
        const instance_part = tkt_instance?.id != null ? String(tkt_instance.id) : 'none';
        return { instanceId: instance_part, userId: String(user.id), section };
    }, [user?.id, tkt_instance?.id, section]);

    const [sidebar_groups, set_sidebar_groups] = useState({});
    // Groups that the app has auto-opened for the current navigation (not persisted).
    // This lets us reveal the group containing the active route without mutating the
    // user preference stored in localStorage. Any user toggle clears the override for
    // that group so the user's choice wins from that point on.
    const [session_open_groups, set_session_open_groups] = useState(() => new Set());

    useEffect(() => {
        if (!sidebar_prefs_scope) {
            set_sidebar_groups({});
            return;
        }
        try {
            const { instanceId, userId, section: sec } = sidebar_prefs_scope;
            set_sidebar_groups(readSidebarSectionOpenMap(userId, instanceId, sec));
        } catch (_err) {
            set_sidebar_groups({});
        }
    }, [sidebar_prefs_scope]);

    // When the active route changes, auto-reveal its sidebar group(s) and release any
    // override on groups that no longer match the route: navigating away from a page
    // whose group was collapsed-in-preference should let that group close back to the
    // user's saved preference. The persisted preference itself is never modified here.
    // We only depend on `pathname` so that the session override is not re-instated
    // after the user has explicitly collapsed the active group (see `toggle_group`).
    useEffect(() => {
        const ids = groupIdsForPathname(pathname, nav_ctx);
        set_session_open_groups(prev => {
            if (prev.size === ids.length && ids.every(id => prev.has(id)))
                return prev;
            return new Set(ids);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);

    // Mobile: when the drawer opens (SideBar mounts) or the route section changes,
    // scroll the active item into view so the user sees where they are.
    useEffect(() => {
        if (!isMobile)
            return undefined;
        const timer = window.setTimeout(() => {
            const active = document.querySelector('.SideBar a.nav-link.active');
            if (active && typeof active.scrollIntoView === 'function') {
                active.scrollIntoView({ block: 'center', behavior: 'auto' });
            }
        }, 50);
        return () => window.clearTimeout(timer);
    }, [isMobile, pathname, section, session_open_groups]);

    const is_group_open = useCallback(
        group_id => session_open_groups.has(group_id) || sidebar_groups[group_id] === true,
        [session_open_groups, sidebar_groups]
    );

    const current_group_ids = useMemo(
        () => new Set(groupIdsForPathname(pathname, nav_ctx)),
        [pathname, nav_ctx]
    );

    const toggle_group = useCallback(
        group_id => {
            // If the group was auto-opened for the current route, clear the override so
            // the user's explicit toggle takes effect immediately and wins from now on.
            set_session_open_groups(prev => {
                if (!prev.has(group_id))
                    return prev;
                const next = new Set(prev);
                next.delete(group_id);
                return next;
            });
            set_sidebar_groups(prev => {
                const was_session_open = session_open_groups.has(group_id);
                const was_open = was_session_open || prev[group_id] === true;
                const next = { ...prev, [group_id]: !was_open };
                try {
                    if (sidebar_prefs_scope) {
                        const { instanceId, userId, section: sec } = sidebar_prefs_scope;
                        writeSidebarSectionOpenMap(userId, instanceId, sec, next);
                    }
                } catch (_err) {
                    // ignore
                }
                return next;
            });
        },
        [sidebar_prefs_scope, session_open_groups]
    );

    useEffect(() => {
        if (section !== Utils.SECTION_OPERATION || !tkt_instance?.hasModule(Utils.MODULE_WORKFLOWS)) {
            return;
        }
        const can_read_workflows = Boolean(
            user?.abilities?.superadmin
            || (typeof user?.can === 'function' && user.can('read', 'workflow_runs'))
        );
        if (!can_read_workflows) {
            return;
        }
        dispatch(loadWorkflows());
    }, [dispatch, section, tkt_instance, user]);

    const toggleChangelog = () => {
        dispatch(updateUi({
            showChangelog: !showChangelog,
            highlightChangelog: false,
        }));
    };

    if (!user)
        return null;

    const render_item = (item) => {
        const label = resolve_label(item, t);
        const extra_class = item.className ? ` ${item.className}` : '';
        const special_active = item.id === 'admin.settings.main'
            ? (() => is_settings_main_nav_active(pathname) ? 'nav-link active' : 'nav-link')
            : undefined;

        return (
            <NavItem key={item.id}>
                <NavLink
                    to={item.path}
                    className={special_active || 'nav-link'}
                    title={collapsed ? label : undefined}
                >
                    {item.iconClass && <i className={item.iconClass}></i>}
                    <span className={`${collapsed ? 'collapsedSpan' : 'expandedSpan'}${extra_class}`}>
                        &nbsp;{label}
                    </span>
                </NavLink>
            </NavItem>
        );
    };

    const render_group = (group) => {
        const group_label = t(group.labelKey);
        return (
        <div key={group.id}>
            <SidebarGroupHeading
                collapsed={collapsed}
                group_id={group.id}
                is_open={is_group_open(group.id)}
                is_current={current_group_ids.has(group.id)}
                on_toggle={toggle_group}
                label={group_label}
            >
                <span className={`${collapsed ? 'h5' : ''}`}>
                    {group.iconClass && <i className={group.iconClass}></i>}
                    <span className={`${collapsed ? 'collapsedSpan' : 'expandedSpan'}`}>
                        &nbsp;{group_label}
                    </span>
                </span>
            </SidebarGroupHeading>
            {is_group_open(group.id) && (
                <Nav className="flex-column" onClick={onCollapseNavClick}>
                    {group.items.filter(item => !item.sidebarHidden).map(render_item)}
                </Nav>
            )}
        </div>
        );
    };

    return (
        <Nav className={`SideBar${collapsed ? ' SideBar--collapsed' : ''}`}>
            <div className="sidebar-sticky d-flex flex-column">
                <div className="sidebar-scroll w-100">
                    {children}
                    <div className={isMobile ? 'SideBar__mobileContextRow' : undefined}>
                        <InstanceSelector
                            onCollapseNavClick={onCollapseNavClick}
                            collapsed={(!isMobile && collapsed) ? collapsed : false}
                        />
                        {!isMobile && canShowSyncImportPanel(user) &&
                            <TktSyncButton collapsed={collapsed} />
                        }
                    </div>
                    <div>
                        {current_section_tree?.groups.map(render_group)}
                    </div>
                </div>
                <div className="sidebar-version-btn-wrap">
                    <Button
                        size="sm"
                        color="info"
                        className={`sidebar-version-btn w-100 text-center rounded-0${highlightChangelog ? ' sidebar-version-btn--glow' : ''}${collapsed ? ' sidebar-version-btn--collapsed' : ''}`}
                        onClick={toggleChangelog}
                        title={highlightChangelog ? t('changelog.whats_new_hint') : t('changelog.changelog')}
                    >
                        {collapsed
                            ? <i className="fa fa-info-circle" aria-hidden="true"></i>
                            : (
                                <>
                                    {Config.app_version
                                        ? <>Version {Config.app_version}</>
                                        : gitBranch ? <>Branch {gitBranch}</> : <>Version</>}
                                    &nbsp;<i className="fa fa-info-circle" aria-hidden="true"></i>
                                </>
                            )}
                    </Button>
                </div>
            </div>
        </Nav>
    );
}

export default SideBar;
